import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Container, Grid, Stack, styled, Typography } from '@mui/material';

import { useDispatch, useSelector } from '../../redux/store';
import { openLoginSignup } from '../../redux/slices/authPages';

import Page from '../../components/Page';
import Image from '../../components/Image';
import conObj from '../../utils/connection-assistant';
import BannerCarousel from './BannerCarousel';
import CustomModal from '../../components/CustomModal';
import { isHTML, sanitizeHtml } from '../../utils/formatString';
import useLocales from '../../hooks/useLocales';
import palette from '../../theme/palette';
import PopupBanner from '../../layouts/dashboard/PopupBanner';
import { closeLoadingScreen, openLoadingScreen } from '../../redux/slices/settings';
import useAuth from '../../hooks/useAuth';
import { SectionHeader, SectionIconWrapper, SectionTitle, ThemeButton } from '../../layouts/GlobalStyles';
import useResponsive from '../../hooks/useResponsive';
import HomeNavCol from './HomeNavCol';
import useTabs from '../../hooks/useTabs';
import HomeBody from './HomeBody';
import Promotion from '../promotion/Promotion';
import LiveTransactionStatus from './LiveTransactionStatus';
import { getTransactionStats } from '../../redux/slices/lookup';
import { DEFAULT_CURRENCY, HEADER } from '../../config';
import Iconify from '../../components/Iconify';
import SpinWheelButton from '../promotion/SpinWheelButton';

const HomeTabRow = styled(Box)(({ theme, isActive }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  width: '100%',
  borderBottom: '2px solid #340606',
  paddingBottom: '4px',
  marginBottom: '2px',
}));

const HomeTabRowBtn = styled(Box)(({ theme, isActive }) => ({
  background: 'linear-gradient(138.07deg, #EB0101 11.32%, #7C0001 96.42%)',
  boxShadow: '0px 0px 5px 0px #FF0000',
  height: '30px',
  // paddingLeft: theme.spacing(4),
  // paddingRight: theme.spacing(4),
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
}));

const RightSectionContainer = styled(Box)(({ theme, isOpenDownload = true }) => ({
  background: 'rgb(45, 3, 3)',
  borderRadius: '8px',
  padding: '12px 10px',
  position: 'sticky',
  top: `calc(${HEADER.MOBILE_HEIGHT}px + 3rem + ${isOpenDownload ? 15 : 0}vw)`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(107deg, rgb(81, 10, 10) -33.89%, rgb(162, 20, 38) 80.56%)',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  color: '#fff',
  width: '100%',
}));

const GradientButtonBorder = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(rgb(192, 103, 39), rgb(221, 169, 84), rgb(179, 28, 42))',
  padding: '3px',
  borderRadius: theme.spacing(1),
  position: 'relative',
  width: '100%',
}));

const GradientButtonIconWrapper = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  marginLeft: '-20px',
  zIndex: 2,
}));

const Home = ({ popupAuth = '0' }) => {
  const navigate = useNavigate();

  const { translate } = useLocales();

  const { isAuthenticated } = useAuth();

  const params = useParams();

  const { siteSettings, transactionStats, promotions, memberBalance } = useSelector((x) => x.lookup);

  const { activeTab } = useSelector((x) => x.game);

  const { isDownloadHeaderOpen } = useSelector((x) => x.settings);

  const dispatch = useDispatch();

  const [allGames, setAllGames] = useState([]);

  const [openPromotion, setOpenPromotion] = useState(null);

  const [isLoadingGames, setIsLoadingGames] = useState(false);

  // const [isLoadingMoreKey, setIsLoadingMoreKey] = useState(-1);

  const gamesRef = useRef(null);

  // const [sortBy, setSortBy] = useState('');

  // const [count, setCount] = useState(0);

  // const [running, setRunning] = useState(true);

  const isDesktop = useResponsive('up', 'lg');

  const { currentTab, setCurrentTab } = useTabs('highlights');

  const [popularGames, setPopularGames] = useState({
    data: [],
    page: 1,
  });

  const [isLoadingPopGames, setIsLoadingPopGames] = useState(false);

  const refDepositBonusId = useMemo(() => {
    const bonuses = promotions?.data?.filter((f) => f.promotion_type === 'referral_deposit_bonus');

    if (bonuses?.length) {
      return bonuses[0]?.id;
    }

    return null;
  }, [promotions]);

  const currentTabComponent = useMemo(() => {
    switch (currentTab) {
      case 'highlights':
        return <HomeBody isLoadingGames={isLoadingGames} popularGames={popularGames} />;
      case 'promotions':
        return <Promotion />;

      default:
        return <HomeBody />;
    }
  }, [currentTab, isLoadingGames, popularGames]);

  useEffect(() => {
    dispatch(openLoadingScreen());
    Promise.all([getPopularGames()]).finally(() => dispatch(closeLoadingScreen()));
    getTransactionStats();
  }, []);

  useEffect(() => {
    const scrollEl = document.getElementById(activeTab);
    if (scrollEl) {
      scrollEl.scrollIntoView({ behavior: 'smooth' });
    }

    handleResetLoadMore();
  }, [activeTab]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (popupAuth === '1') dispatch(openLoginSignup({ open: true, isLogin: true }));
      if (popupAuth === '2') dispatch(openLoginSignup({ open: true, isLogin: false }));
    }
  }, [isAuthenticated, popupAuth]);

  useEffect(() => {
    if (params?.section) setCurrentTab('highlights');
  }, [params?.section]);

  const getPopularGames = () => {
    setIsLoadingPopGames(true);
    conObj
      .post(`/games/items.json?sort=popularity&limit=${window.innerWidth < 500 ? 12 : 18}&page=${popularGames?.page}`)
      .then((res) => {
        if (res.status === 200) {
          setPopularGames((prevState) => ({
            data: [...prevState?.data, ...res.data?.data?.data],
            page: prevState?.page + 1,
          }));
          setIsLoadingPopGames(false);
        }
      })
      .catch((err) => {
        setIsLoadingPopGames(false);
        console.log(`Error: ${err.message}`);
      });
  };

  const handleResetLoadMore = () => {
    if (allGames.filter((f) => f.showCount > 12).length > 0) {
      setAllGames([...allGames.map((item) => ({ ...item, showCount: 12, currentPage: 1 }))]);
    }
  };

  return (
    <Page title="Home" sx={{ position: 'relative' }}>
      {process.env.REACT_APP_SHOW_SPINWHEEL_BUTTON === 'yes' && (
        <SpinWheelButton sx={{ position: 'fixed', top: '50%', right: '3%', zIndex: 9 }} />
      )}
      <Box sx={{ width: '100%', px: { sm: 2, xs: 0 } }}>
        <BannerCarousel />

        {!isAuthenticated && !isDesktop && (
          <Box sx={{ width: '100%', display: 'flex', gap: 1, px: 1, mt: 1.5 }}>
            <ThemeButton
              sx={{ background: 'linear-gradient(180deg, #FFE11E 45.67%, #F19119 56.25%)', px: 2, width: '50%' }}
              onClick={() => dispatch(openLoginSignup({ open: true, isLogin: false }))}
            >
              {translate('sign_up')}
            </ThemeButton>
            <ThemeButton
              onClick={() => dispatch(openLoginSignup({ open: true, isLogin: true }))}
              sx={{ color: palette.dark.common.white, px: 2, width: '50%' }}
            >
              {translate('login')}
            </ThemeButton>
          </Box>
        )}
      </Box>

      {/* <Box sx={{ mt: 1 }}>
        <SubHeaderNav />
      </Box> */}

      <Grid
        container
        spacing={1}
        sx={{
          px: { xs: 1, sm: 4 },
          mt: 1.5,
        }}
      >
        <Grid item xs={12} sm={9}>
          {siteSettings?.announcement_text?.WalkPromotion?.content && (
            <Box
              sx={{
                // px: { sm: '16px !important', xs: '8px !important' },
                mb: 1.5,
              }}
            >
              <Stack
                direction="row"
                alignItems={'center'}
                sx={{
                  p: '0px 8px',
                  background: palette.home.announcement.bg,
                  borderRadius: 0.5,
                  cursor: siteSettings?.announcement_text?.WalkPromotion?.url ? 'pointer' : 'none',
                }}
                onClick={() => {
                  if (siteSettings?.announcement_text?.WalkPromotion?.url) {
                    window.open(siteSettings?.announcement_text?.WalkPromotion?.url, '_blank');
                  }
                }}
              >
                <Image src={require('../../assets/home/bell.png')} sx={{ height: '32px', width: '35px' }} />
                <Box className="marquee" sx={{ width: '100%' }}>
                  <Typography sx={{ color: palette.home.announcement.color, fontWeight: 400, fontSize: '0.8rem' }}>
                    {siteSettings?.announcement_text?.WalkPromotion?.content}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
          <Box ref={gamesRef} />
          <Grid container spacing={1}>
            {!params?.section ? (
              <Grid item xs={12} sm={0} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <HomeTabRow>
                  <HomeTabRowBtn isActive={currentTab === 'highlights'} onClick={() => setCurrentTab('highlights')}>
                    <Image src={require('../../assets/home/highlights.png')} sx={{ width: '13px', height: '13px' }} />
                    {translate('highlights')}
                  </HomeTabRowBtn>
                  <HomeTabRowBtn
                    // isActive={currentTab === 'promotions'}
                    onClick={() => navigate('/promotion')}
                  >
                    <Image src={require('../../assets/home/promotion.png')} sx={{ width: '13px', height: '13px' }} />
                    {translate('promotions')}
                  </HomeTabRowBtn>
                </HomeTabRow>
              </Grid>
            ) : (
              ''
            )}
            <Grid item xs={2} sm={2} md={0} sx={{ display: { xs: 'flex', sm: 'flex', md: 'none' } }}>
              <HomeNavCol />
            </Grid>
            <Grid item xs={10} sm={10} md={12}>
              {currentTabComponent}
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={3}>
          <RightSectionContainer isOpenDownload={isDownloadHeaderOpen && !isDesktop}>
            <Box
              sx={{
                background: 'radial-gradient(circle at 50% -30%, rgb(213, 30, 0) 0%, rgba(70, 7, 7, 0) 60%)',
                borderTopRightRadius: '8px',
                borderTopLeftRadius: '8px',
                height: '150px',
                width: '100%',
                top: 0,
                left: 0,
                position: 'absolute',
                zIndex: 1,
              }}
            />
            <Box sx={{ zIndex: 2, position: 'relative' }}>
              <SectionHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <SectionIconWrapper sx={{ width: '16px !important', minWidth: 'unset' }}>
                    {/* <Image src={require('../../assets/menu/leaderboard.png')} sx={{ width: '100%' }} /> */}
                    <Iconify icon={'mingcute:trophy-fill'} sx={{ fontSize: '14px' }} />
                  </SectionIconWrapper>
                  <Typography sx={{ fontSize: '14px' }}>{translate('live_transactions')}</Typography>
                </Box>
              </SectionHeader>
              <LiveTransactionStatus
                transactionStats={transactionStats}
                currencyCode={memberBalance?.currencyCode || DEFAULT_CURRENCY}
                translate={translate}
              />
            </Box>

            {isDesktop && (
              <Box sx={{ zIndex: 2, position: 'relative' }}>
                <SectionHeader>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <SectionIconWrapper sx={{ width: '16px !important', minWidth: 'unset' }}>
                      {/* <Image src={require('../../assets/menu/leaderboard.png')} sx={{ width: '100%' }} /> */}
                      <Iconify icon={'mdi:target'} sx={{ fontSize: '14px' }} />
                    </SectionIconWrapper>
                    <Typography sx={{ fontSize: '14px' }}>{translate('challenges')}</Typography>
                  </Box>
                </SectionHeader>
                <Grid container spacing={1} sx={{ py: 1 }}>
                  <Grid item xs={6} sx={{ position: 'relative' }}>
                    <GradientButtonBorder
                      sx={{ borderRadius: '2rem' }}
                      onClick={() => {
                        if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                        else navigate('/user/missions');
                      }}
                    >
                      <GradientButton size="large" sx={{ borderRadius: '2rem' }}>
                        <GradientButtonIconWrapper>
                          <Image src={require('../../assets/home/nav-events.png')} sx={{ width: '100%' }} />
                        </GradientButtonIconWrapper>
                        {translate('missions')}
                      </GradientButton>
                    </GradientButtonBorder>
                  </Grid>
                  <Grid item xs={6} sx={{ position: 'relative' }}>
                    <GradientButtonBorder
                      sx={{ borderRadius: '2rem' }}
                      onClick={() => {
                        if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                        else navigate('/user/rewards');
                      }}
                    >
                      <GradientButton size="large" sx={{ borderRadius: '2rem' }}>
                        <GradientButtonIconWrapper>
                          <Image src={require('../../assets/home/leaderboard.png')} sx={{ width: '100%' }} />
                        </GradientButtonIconWrapper>
                        {translate('rewards')}
                      </GradientButton>
                    </GradientButtonBorder>
                  </Grid>
                </Grid>
              </Box>
            )}
          </RightSectionContainer>
        </Grid>

        {/* <Box sx={{ mt: 2, mb: 2 }}>
          <Box sx={{ backgroundColor: 'rgb(49, 48, 64)' }}>
            <Typography sx={{ fontWeight: 500, padding: '1rem 0 0 1rem' }}>{translate('Testimonials')}</Typography>
            <Stack direction="row" spacing={2} sx={{ overflowX: 'auto' }} p={'1rem'}>
              <Stack flex={0}>
                <TestimonialContainer number={1}>
                  <Stack mb={1.5} direction={'row'} justifyContent={'space-between'} alignItems={'center'} spacing={1}>
                    <TestimonialCaption sx={{ fontWeight: 700 }}>The best you could ask for!</TestimonialCaption>
                    <Image
                      src={require('../../assets/brand-logo.png')}
                      alt=""
                      sx={{ height: '25px', width: '59px', objectFit: 'contain!important' }}
                    />
                  </Stack>
                  <TestimonialCaption>
                    I have played on various platforms, yet none have captivated me like Yum998. It stands head and
                    shoulders above the competition in every aspect, from its exceptional game selection to its
                    remarkable promotional offerings, never ceasing to astonish me.
                  </TestimonialCaption>
                  <Stack spacing={0.5} direction={'row'} mt={1}>
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                  </Stack>
                </TestimonialContainer>
              </Stack>
              <Stack flex={0}>
                <TestimonialContainer number={2}>
                  <Stack mb={1.5} direction={'row'} justifyContent={'space-between'} alignItems={'center'} spacing={1}>
                    <TestimonialCaption sx={{ fontWeight: 700 }}>The best you could ask for!</TestimonialCaption>
                    <Image
                      src={require('../../assets/brand-logo.png')}
                      alt=""
                      sx={{ height: '25px', width: '59px', objectFit: 'contain!important' }}
                    />
                  </Stack>
                  <TestimonialCaption>
                    I have played on various platforms, yet none have captivated me like Yum998. It stands head and
                    shoulders above the competition in every aspect, from its exceptional game selection to its
                    remarkable promotional offerings, never ceasing to astonish me.
                  </TestimonialCaption>
                  <Stack spacing={0.5} direction={'row'} mt={1}>
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                    <Iconify icon={'carbon:star-filled'} width={20} height={20} />
                  </Stack>
                </TestimonialContainer>
              </Stack>
            </Stack>
          </Box>
        </Box> */}

        {/* <Box
          sx={{
            mt: 2,
            mb: 5,
            py: '1rem',
            backgroundColor: 'rgb(49, 48, 64)',
          }}
        >
          <Typography px={'1rem'} sx={{ fontWeight: 500, marginBottom: 2 }}>{translate('Recent Big Wins')}</Typography>
          <StatsContainer>
            <Stack direction="row" spacing={1} px={'1rem'} pb={'1rem'}>
              {fakePlayerStats?.slice(0, 10)?.map((item, index) => (
                <Stack sx={{ width: '9rem' }} spacing={0.5} key={index}>
                  <Image
                    src={item?.game_photo_url}
                    sx={{
                      borderRadius: '10px',
                      width: '9rem',
                      height: '9rem',
                    }}
                  />
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#4cef4c', textAlign: 'center' }}>
                    {process.env.REACT_APP_CURRENCY_CODE}
                    {fNumberCommaSeparated(item?.win)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </StatsContainer>
        </Box> */}
      </Grid>

      <CustomModal
        open={!!openPromotion}
        onClose={() => setOpenPromotion(null)}
        children={
          <Stack spacing={4} sx={{ p: 2 }}>
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <Typography className="gold-text" variant="h4" fontWeight={900}>
                {openPromotion?.title}
              </Typography>
            </Box>

            <Box sx={{ width: '100%' }}>
              {openPromotion?.description && isHTML(openPromotion?.description) ? (
                <Box
                  sx={{
                    '& ol, ul': { listStylePosition: 'inside', overflow: 'hidden' },
                    '& li p': { display: 'inline' },
                  }}
                  dangerouslySetInnerHTML={sanitizeHtml(openPromotion?.description)}
                />
              ) : (
                <Typography>{openPromotion?.description || ''}</Typography>
              )}
            </Box>
          </Stack>
        }
      />

      <PopupBanner />
    </Page>
  );
};

export default Home;
